import { useMemo, useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { ArrowLeft, MapPin, Pencil, Plus, ClipboardList, FileText, Lock, LockOpen, CalendarDays, Eye } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { PageLayout } from "@/components/templates/page-layout";
import { SortableZoneCard } from "@/components/molecules/sortable-zone-card";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/molecules/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { offlineDb } from "@/core/offline/db";
import { useZones } from "@/core/hooks/useZones";
import { useCampagneActive } from "@/core/hooks/useCampagneActive";
import { useViewedCampagne } from "@/core/hooks/useViewedCampagne";


export default function SiteDetailPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const site = useLiveQuery(() => offlineDb.sites.get(siteId!), [siteId]);
  const { zones, deleteZone, reorderZones } = useZones(siteId!);
  const { campagne, campagnes, closeCampagne, reopenCampagne, createNewCampagne } = useCampagneActive(siteId!);
  const { viewedCampagne, isViewingHistorical } = useViewedCampagne(siteId!);
  const [searchParams] = useSearchParams();
  const viewedCampagneIdParam = searchParams.get("campagneId");
  const [zoneToDelete, setZoneToDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showCloseSiteConfirm, setShowCloseSiteConfirm] = useState(false);
  const [showNewCampagneConfirm, setShowNewCampagneConfirm] = useState(false);
  const [campagneError, setCampagneError] = useState("");

  const siteIsClosed = !campagne && campagnes.length > 0;
  const siteIsActive = !!campagne && campagne.status === "active";
  const closedCampagnes = campagnes.filter((c) => c.status === "cloturee").sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const latestClosedCampagne = closedCampagnes[0];

  // Drag-and-drop des zones. La poignée a une contrainte de 8px : un simple tap
  // ne déclenche pas un drag (la carte reste cliquable). KeyboardSensor pour
  // l'accessibilité (réordonner au clavier).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  async function handleZonesDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = zones.findIndex((z) => z.id === active.id);
    const newIndex = zones.findIndex((z) => z.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(zones, oldIndex, newIndex).map((z) => z.id);
    await reorderZones(reordered);
  }

  async function handleCloseSite() {
    if (!campagne) return;
    await closeCampagne(campagne.id);
    setShowCloseSiteConfirm(false);
  }

  async function handleReopen() {
    if (!latestClosedCampagne) return;
    await reopenCampagne(latestClosedCampagne.id);
  }

  async function handleNewCampagne() {
    setCampagneError("");
    try {
      await createNewCampagne();
      setShowNewCampagneConfirm(false);
    } catch (err) {
      setCampagneError(err instanceof Error ? err.message : "Erreur");
    }
  }

  // Compter les mesures par zone (modèle B : pin.zoneId direct)
  const mesureCountsRaw = useLiveQuery(
    async () => {
      if (zones.length === 0) return {};
      const counts: Record<string, number> = {};
      for (const zone of zones) {
        const pins = await offlineDb.pins.where("zoneId").equals(zone.id).toArray();
        const pinIds = pins.map((p) => p.id);
        counts[zone.id] = pinIds.length === 0
          ? 0
          : await offlineDb.mesures.where("pinId").anyOf(pinIds).count();
      }
      return counts;
    },
    [zones]
  );

  const mesureCountsByZone = mesureCountsRaw ?? {};

  const totalMesures = useMemo(
    () => Object.values(mesureCountsRaw ?? {}).reduce((sum, n) => sum + n, 0),
    [mesureCountsRaw]
  );

  // Scroll lock + Escape quand le modal est ouvert
  useEffect(() => {
    if (!zoneToDelete) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setZoneToDelete(null); setDeleteError(""); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [zoneToDelete]);

  const handleConfirmDeleteZone = useCallback(async () => {
    if (!zoneToDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteZone(zoneToDelete);
      setZoneToDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  }, [zoneToDelete, deleteZone]);

  if (!site) {
    return (
      <PageLayout>
        <p className="text-muted-foreground">Site introuvable.</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Bandeau consultation campagne historique */}
      {isViewingHistorical && viewedCampagne && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
          <Eye className="size-4 text-amber-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-amber-900">
              Consultation de la campagne du {new Date(viewedCampagne.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} (clôturée)
            </div>
            <div className="text-xs text-amber-700 mt-0.5">
              Les données affichées sont figées à cette date.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/sites/${siteId}`)}
            className="shrink-0"
          >
            Campagne actuelle
          </Button>
        </div>
      )}

      {/* Header avec retour */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/sites")}
          className="mb-3 flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ArrowLeft className="size-4" />
          Retour aux sites
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{site.name}</h2>
            {site.client && (
              <p className="text-sm text-muted-foreground">
                Client : {site.client}
              </p>
            )}
            {site.address && (
              <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                <MapPin className="size-3.5" />
                {site.address}
              </p>
            )}
            {totalMesures > 0 && (
              <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                <ClipboardList className="size-3.5" />
                {totalMesures} mesure{totalMesures > 1 ? "s" : ""} au total
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            className="tap-target rounded-lg ring-1 ring-foreground/8"
            onClick={() => navigate(`/sites/${siteId}/modifier`)}
          >
            <Pencil className="size-4" />
          </Button>
        </div>
      </div>

      {/* Section Campagne */}
      <div className="mb-6 p-4 bg-card rounded-xl border border-border">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="size-4 text-muted-foreground" />
              <h3 className="font-semibold">
                {siteIsActive ? "Campagne en cours" : siteIsClosed ? "Site clôturé" : "Aucune campagne"}
              </h3>
              {siteIsActive && (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  Active
                </Badge>
              )}
              {siteIsClosed && (
                <Badge variant="secondary" className="text-xs">
                  <Lock className="mr-1 size-3" />
                  Clôturé
                </Badge>
              )}
            </div>
            {campagne && (
              <p className="text-sm text-muted-foreground">
                Démarrée le {new Date(campagne.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}
            {siteIsClosed && latestClosedCampagne && (
              <p className="text-sm text-muted-foreground">
                Clôturée le {new Date(latestClosedCampagne.updatedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            {siteIsActive && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCloseSiteConfirm(true)}
              >
                <Lock className="size-4" />
                Clôturer le site
              </Button>
            )}
            {siteIsClosed && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReopen}
                  title="Rouvre la dernière campagne pour corriger des mesures"
                >
                  <LockOpen className="size-4" />
                  Déverrouiller
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowNewCampagneConfirm(true)}
                >
                  <Plus className="size-4" />
                  Nouvelle campagne
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Historique : campagnes clôturées précédentes */}
        {closedCampagnes.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Historique ({closedCampagnes.length} campagne{closedCampagnes.length > 1 ? "s" : ""})
            </p>
            <ul className="space-y-1">
              {closedCampagnes.slice(0, 5).map((c) => (
                <li key={c.id} className="text-sm flex items-center gap-2 text-muted-foreground">
                  <Lock className="size-3" />
                  {new Date(c.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  {c.notes && <span className="truncate">— {c.notes}</span>}
                </li>
              ))}
              {closedCampagnes.length > 5 && (
                <li className="text-xs text-muted-foreground italic">
                  … et {closedCampagnes.length - 5} plus ancienne{closedCampagnes.length - 5 > 1 ? "s" : ""}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Bouton Rapport PDF */}
      <button
        onClick={() => navigate(`/sites/${siteId}/rapport`)}
        className="w-full mb-6 flex items-center gap-4 p-4 bg-card rounded-xl border border-border hover:border-primary/30 hover:shadow-md transition-all group"
      >
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/15">
          <FileText className="size-6 text-primary" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-semibold text-base">Rapport PDF</div>
          <div className="text-sm text-muted-foreground">
            Rédiger et exporter le rapport de diagnostic
          </div>
        </div>
        <svg
          className="size-5 text-muted-foreground/30 group-hover:text-primary"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Section zones */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          Zones ({zones.length})
        </h3>
        <Button
          size="sm"
          onClick={() => navigate(`/sites/${siteId}/zones/nouveau`)}
        >
          <Plus className="size-4" />
          Ajouter
        </Button>
      </div>

      {zones.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          Aucune zone pour l&apos;instant. Ajoutez votre première zone !
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleZonesDragEnd}
        >
          <SortableContext
            items={zones.map((z) => z.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-3">
              {zones.map((zone) => (
                <SortableZoneCard
                  key={zone.id}
                  id={zone.id}
                  name={zone.name}
                  planType={zone.planType}
                  planBlob={zone.planBlob}
                  mesureCount={mesureCountsByZone[zone.id]}
                  onClick={() =>
                    navigate(
                      viewedCampagneIdParam
                        ? `/sites/${siteId}/zones/${zone.id}?campagneId=${viewedCampagneIdParam}`
                        : `/sites/${siteId}/zones/${zone.id}`
                    )
                  }
                  onEdit={() =>
                    navigate(`/sites/${siteId}/zones/${zone.id}/modifier`)
                  }
                  onDelete={() => setZoneToDelete(zone.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {/* Modale de confirmation de suppression */}
      <ConfirmDialog
        open={!!zoneToDelete}
        title="Supprimer cette zone ?"
        description="La zone et toutes ses campagnes, points et mesures seront définitivement supprimés. Cette action est irréversible."
        error={deleteError}
        loading={deleting}
        onConfirm={handleConfirmDeleteZone}
        onCancel={() => { setZoneToDelete(null); setDeleteError(""); }}
      />

      {/* Modal Clôturer le site */}
      <ConfirmDialog
        open={showCloseSiteConfirm}
        title="Clôturer le site ?"
        description="La campagne en cours sera figée. Toutes les zones deviendront non modifiables (pas de nouveaux pins, pas de modification de mesures). Tu pourras toujours déverrouiller ou démarrer une nouvelle campagne."
        confirmLabel="Clôturer"
        destructive={false}
        onConfirm={handleCloseSite}
        onCancel={() => setShowCloseSiteConfirm(false)}
      />

      {/* Modal Nouvelle campagne */}
      <ConfirmDialog
        open={showNewCampagneConfirm}
        title="Démarrer une nouvelle campagne ?"
        description="Les pins et leur fiche d'identité (localisation, matériau, DN, diamètre, épaisseur d'origine) sont repris de la dernière campagne. Les épaisseurs min/max, photos et notes sont remises à zéro pour que tu puisses saisir les nouvelles mesures."
        confirmLabel="Démarrer"
        destructive={false}
        error={campagneError}
        onConfirm={handleNewCampagne}
        onCancel={() => { setShowNewCampagneConfirm(false); setCampagneError(""); }}
      />
    </PageLayout>
  );
}
